import { AlertCircle, CheckCircle2, Info, Loader2 } from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { useMeshAnalysis } from '@/hooks/useMeshAnalysis';
import { useCollisionDetection } from '@/hooks/useCollisionDetection';
import { useBlob } from '@/contexts/BlobContext';
import { cn } from '@/lib/utils';

interface PrintabilityStatusProps {
  className?: string;
}

/** Converts an RGBA 0-1 tuple to a CSS color string. */
function rgbaToCss(color: [number, number, number, number]): string {
  const [r, g, b, a] = color;
  return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})`;
}

/** Small inline color swatch. */
function ColorSwatch({ color }: { color: [number, number, number, number] }) {
  return (
    <span
      className="inline-block h-3 w-3 shrink-0 rounded-sm border border-black/20"
      style={{ backgroundColor: rgbaToCss(color) }}
      aria-hidden="true"
    />
  );
}

export function PrintabilityStatus({ className }: PrintabilityStatusProps) {
  const { blob, coloredBlobs } = useBlob();
  const { isAnalyzing, result, error } = useMeshAnalysis(blob);
  const {
    isChecking,
    result: collisionResult,
    error: collisionError,
  } = useCollisionDetection(coloredBlobs);

  if (!blob) return null;

  const isBusy = isAnalyzing || isChecking;

  if (isBusy) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 text-xs text-muted-foreground',
          className,
        )}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Analyzing mesh...</span>
      </div>
    );
  }

  if (error || collisionError) {
    const errorMessage = error ?? collisionError;
    return (
      <HoverCard>
        <HoverCardTrigger asChild>
          <div
            className={cn(
              'flex cursor-help items-center gap-1.5 text-xs text-red-500',
              className,
            )}
          >
            <AlertCircle className="h-3.5 w-3.5" />
            <span>Analysis Error</span>
          </div>
        </HoverCardTrigger>
        <HoverCardContent className="w-80" side="top">
          <div className="space-y-2">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-red-500">
              <AlertCircle className="h-4 w-4" />
              Printability Analysis Failed
            </h4>
            <p className="text-sm text-muted-foreground">{errorMessage}</p>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  if (!result) return null;

  // Determine status and messages
  const minDimension = Math.min(...result.boundingBox.size);
  const issues: {
    type: 'error' | 'warning' | 'info';
    message: string;
    /** Optional inline elements to render after the message text. */
    detail?: React.ReactNode;
  }[] = [];

  if (result.openEdges > 0) {
    issues.push({
      type: 'error',
      message: `Found ${result.openEdges} open edges. The model is not watertight and may not slice correctly.`,
    });
  }

  if (result.nonManifoldEdges > 0) {
    issues.push({
      type: 'error',
      message: `Found ${result.nonManifoldEdges} non-manifold edges. Geometry has zero-thickness walls or intersecting faces.`,
    });
  }

  if (minDimension < 0.4 && minDimension > 0.001) {
    // 0.001 to ignore effectively 2D planes generated by mistake
    issues.push({
      type: 'warning',
      message: `Minimum dimension (${minDimension.toFixed(2)}mm) is below typical nozzle size (0.4mm). Features might be too thin to print.`,
    });
  }

  if (result.degenerateFaces > 0) {
    issues.push({
      type: 'info',
      message: `Found ${result.degenerateFaces} degenerate (zero-area) faces. This is usually fine but can sometimes cause slicing artifacts.`,
    });
  }

  // Collision detection: one error entry per intersecting pair.
  if (collisionResult && collisionResult.intersectingPairs.length > 0) {
    for (const pair of collisionResult.intersectingPairs) {
      issues.push({
        type: 'error',
        message: 'Two bodies overlap and cannot be assembled as printed.',
        detail: (
          <span className="mt-1 flex items-center gap-1.5">
            <ColorSwatch color={pair.colorA} />
            <span className="text-muted-foreground">and</span>
            <ColorSwatch color={pair.colorB} />
          </span>
        ),
      });
    }
  }

  const hasErrors = issues.some((i) => i.type === 'error');
  const hasWarnings = issues.some((i) => i.type === 'warning');

  const StatusIcon = hasErrors
    ? AlertCircle
    : hasWarnings
      ? AlertCircle
      : CheckCircle2;

  const statusColor = hasErrors
    ? 'text-red-500'
    : hasWarnings
      ? 'text-yellow-500'
      : 'text-green-500';

  const statusText = hasErrors
    ? 'Printability Issues'
    : hasWarnings
      ? 'Printability Warnings'
      : 'Print Ready';

  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <div
          className={cn(
            'flex cursor-help items-center gap-1.5 text-xs transition-colors hover:opacity-80',
            statusColor,
            className,
          )}
        >
          <StatusIcon className="h-4 w-4" />
          <span className="font-medium">{statusText}</span>
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-80" side="top" align="end">
        <div className="space-y-3">
          <div className="flex items-center gap-2 border-b pb-2">
            <StatusIcon className={cn('h-5 w-5', statusColor)} />
            <h4 className="font-semibold">{statusText}</h4>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex flex-col">
              <span className="text-muted-foreground">Dimensions (mm)</span>
              <span>
                {result.boundingBox.size[0].toFixed(1)} ×{' '}
                {result.boundingBox.size[1].toFixed(1)} ×{' '}
                {result.boundingBox.size[2].toFixed(1)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground">Triangles</span>
              <span>{result.triangleCount.toLocaleString()}</span>
            </div>
          </div>

          {issues.length > 0 ? (
            <div className="space-y-2 border-t pt-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Analysis Details
              </span>
              <ul className="space-y-2">
                {issues.map((issue, idx) => (
                  <li
                    key={`${issue.type}-${idx}`}
                    className="flex gap-2 text-sm leading-snug"
                  >
                    {issue.type === 'error' && (
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                    )}
                    {issue.type === 'warning' && (
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500" />
                    )}
                    {issue.type === 'info' && (
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                    )}
                    <span
                      className={cn(
                        'flex flex-col',
                        issue.type === 'error' && 'text-red-500',
                        issue.type === 'warning' &&
                          'text-yellow-600 dark:text-yellow-500',
                        issue.type === 'info' && 'text-muted-foreground',
                      )}
                    >
                      {issue.message}
                      {issue.detail}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="border-t pt-2">
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                No common printability issues detected.
              </p>
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
